import { useState, useEffect, useMemo, useReducer } from "react";
import { Offcanvas, Row, Col, Button } from "react-bootstrap";
import { FaCloudDownloadAlt, FaTrash, FaExclamationCircle } from "react-icons/fa";

import useFetchData from "./useFetchDatav2";
import { useToast } from "../Context/useToast";
import { format, parseISO } from "date-fns";
import "../apps/InvoiceStatements.css";
import PdfModal from "./PdfModal";
import ModalDialogue from "./ModalDialogue";
import InvoiceStatementConfigureModal from "./InvoiceStatementConfigureModal";

// use this for the modal dialogue - it's an object
const initialModalDialgueState = {
   isModalDialogueOpen: false,
   question: "",
};

// reducer function for modal dialogue state
const modalDialogueReducer = (state, action) => {
   switch (action.type) {
      case "OPEN_MODAL":
         return { ...state, isModalDialogueOpen: true, question: action.payload.question, goBackText: action.payload.goBackText || "No, go back", goText: action.payload.goText || "Yes Delete it!" };
      case "CLOSE_MODAL":
         return { ...state, isModalDialogueOpen: false };
      default:
         return state;
   }
};

// ─── Module-scope supplier resolution helpers ─────────────────────────────────

const normalizeSupplierName = (name) => {
   if (!name) return "";
   return name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[®™©℠]/g, "")
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
};

const normalizeCountryCode = (value) => {
   if (!value) return "";
   const map = {
      canada: "CA",
      "united states": "US",
      "united states of america": "US",
      usa: "US",
      us: "US",
      ca: "CA",
   };
   return map[value.toLowerCase().trim()] ?? value.toUpperCase().trim();
};

const toBool = (value) => {
   if (typeof value === "boolean") return value;
   if (value == null) return false;
   const text = String(value).trim().toLowerCase();
   return text === "1" || text === "true" || text === "yes";
};

// ─── Component ────────────────────────────────────────────────────────────────

const InvoiceStatementSetupModal = ({ dpaId, docId, onDeleted }) => {

   const [show, setShow] = useState(true);
   const [modalDialgueState, dispatchModal] = useReducer(modalDialogueReducer, initialModalDialgueState);
   const { isModalDialogueOpen, question } = modalDialgueState;
   const [isDeleting, setIsDeleting] = useState(false);
   const [showConfigureModal, setShowConfigureModal] = useState(false);
   const { fetchData } = useFetchData();
   const { triggerToast } = useToast();

   const [isLoadingInvSt, setIsLoadingInvSt] = useState(true);
   const [isProcessing, setIsProcessing] = useState(false);
   const [setupData, setSetupData] = useState(null);
   const [payloadInvSt, setPayloadInvSt] = useState(null);

   const [showStatementPdfModal, setShowStatementPdfModal] = useState(false);
   const [statementImageData, setStatementImageData] = useState(null);

   // Derive panel config from supplier resolution signals (Case A / B / C)
   const panelConfig = useMemo(() => {
      const onboarding = payloadInvSt?.document_onboarding || {};
      const labels = onboarding?.profile?.labels || [];
      const candidate = onboarding?.profile?.supplier_candidates?.[0] || null;

      const getLabelText = (labelNames = []) => {
         for (const labelName of labelNames) {
            const found = labels.find((item) => item?.label === labelName && item?.text);
            if (found?.text) return found.text;
         }
         return "";
      };

      const extractedParent =
         payloadInvSt?.supplier_parent_name ||
         getLabelText(["SupplierParentName"]) ||
         onboarding?.claude_raw?.supplier?.parent ||
         onboarding?.onboarding_payload?.supplier_parent_name ||
         candidate?.parent_name ||
         payloadInvSt?.detected_supplier_parent_name ||
         payloadInvSt?.supplier_parent_normalized ||
         "";

      const extractedTrading =
         payloadInvSt?.supplier_trading_name ||
         getLabelText(["SupplierTradingName"]) ||
         onboarding?.claude_raw?.supplier?.trading ||
         onboarding?.onboarding_payload?.supplier_trading_name ||
         candidate?.trading_name ||
         payloadInvSt?.detected_supplier_trading_name ||
         payloadInvSt?.supplier_trading_normalized ||
         "";

      const extractedAddress =
         payloadInvSt?.supplier_trading_address ||
         getLabelText(["SupplierTradingAddress", "SupplierParentAddress"]) ||
         onboarding?.claude_raw?.supplier_address ||
         onboarding?.onboarding_payload?.supplier_address ||
         candidate?.address ||
         payloadInvSt?.detected_supplier_address ||
         payloadInvSt?.supplier_address_normalized ||
         "";

      const suggestedSupplierName = payloadInvSt?.suggested_supplier_name || "";
      const typeaheadPrefill = extractedParent || extractedTrading || suggestedSupplierName || "";

      const extracted = {
         parentName: extractedParent,
         tradingName: extractedTrading,
         address: extractedAddress,
      };

      const fields = [
         { key: "supplier_parent_name", label: "Extracted parent supplier", value: extractedParent, editable: false },
         { key: "supplier_trading_name", label: "Extracted trading supplier", value: extractedTrading, editable: false },
         { key: "supplier_address", label: "Extracted address", value: extractedAddress, editable: false },
      ];

      const supplierResolved = Boolean(payloadInvSt?.dpa_supplier_resolved) && payloadInvSt?.dpa_master_supplier_id != null;
      const hasSuggested = payloadInvSt?.dpa_suggested_master_supplier_id != null || String(payloadInvSt?.dpa_supplier_resolution_status || "").toLowerCase() === "ml_needs_review";

      if (supplierResolved) {
         return {
            panel_mode: "onboard",
            supplier_status: "complete",
            title: "Layout setup required",
            message: "We identified the supplier for this statement, but the statement layout does not match an existing configuration. Please continue onboarding to confirm the layout and table fields.",
            show_supplier_typeahead: false,
            supplier_typeahead_label: "",
            supplier_typeahead_prefill: "",
            fields,
            extracted,
            actions: {
               primary: "Configure Onboarding",
               secondary: ["Download", "Delete"],
            },
            icon: "info",
         };
      }

      if (hasSuggested) {
         return {
            panel_mode: "review",
            supplier_status: "review",
            title: "Supplier review required",
            message: "We found a possible supplier for this statement, but confidence is not high enough to continue automatically. Please confirm the supplier below or search for the correct supplier.",
            show_supplier_typeahead: true,
            supplier_typeahead_label: "Find Parent Supplier",
            supplier_typeahead_prefill: typeaheadPrefill,
            fields,
            extracted,
            actions: {
               primary: "Continue Review",
               secondary: ["Supplier not found", "Download", "Delete"],
            },
            icon: "warning",
         };
      }

      return {
         panel_mode: "onboard",
         supplier_status: "missing",
         title: "Supplier setup required",
         message: "We could not confidently identify the supplier for this statement. Review the extracted values below and search for the correct supplier.",
         show_supplier_typeahead: true,
         supplier_typeahead_label: "Find Parent Supplier",
         supplier_typeahead_prefill: typeaheadPrefill,
         fields,
         extracted,
         actions: {
            primary: "Configure Onboarding",
            secondary: ["Supplier not found", "Download", "Delete"],
         },
         icon: "warning",
      };
   }, [payloadInvSt]);

   const parseAddressParts = (supplier, fallbackAddressText = "") => {
      if (supplier && (supplier.street || supplier.city || supplier.province || supplier.postal_code || supplier.country)) {
         return {
            street: supplier.street || "",
            city: supplier.city || "",
            province: supplier.province || supplier.state || "",
            postalCode: supplier.postal_code || supplier.zip_code || "",
            country: supplier.country || "",
         };
      }

      const raw = String(fallbackAddressText || "").trim();
      if (!raw) return { street: "", city: "", province: "", postalCode: "", country: "" };

      const lines = raw
         .split(/\n+/)
         .map((x) => x.trim())
         .filter(Boolean);

      const street = (lines[0] || raw.split(",")[0] || "").trim();
      const tail = lines.length > 1 ? lines.slice(1).join(" ") : raw.replace(street, "").trim();

      let city = "";
      let province = "";
      let postalCode = "";
      let country = "";

      const postalMatch = tail.match(/\b([A-Z]\d[A-Z][ -]?\d[A-Z]\d|\d{5}(?:-\d{4})?)\b/i);
      if (postalMatch) postalCode = postalMatch[1].toUpperCase();

      const countryMatch = tail.match(/\b(CA|CANADA|US|USA|UNITED STATES)\b/i);
      if (countryMatch) {
         const c = countryMatch[1].toUpperCase();
         country = c === "CANADA" ? "CA" : c === "UNITED STATES" ? "US" : c;
      }

      const provinceMatch = tail.match(/\b(AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT|AL|AK|AZ|AR|CA|CO|CT|DC|DE|FL|GA|HI|IA|ID|IL|IN|KS|KY|LA|MA|MD|ME|MI|MN|MO|MS|MT|NC|ND|NE|NH|NJ|NM|NV|NY|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VA|VT|WA|WI|WV|WY)\b/i);
      if (provinceMatch) province = provinceMatch[1].toUpperCase();

      const cityCandidate = tail
         .replace(postalCode, "")
         .replace(province, "")
         .replace(country, "")
         .replace(/[,]+/g, " ")
         .trim();
      if (cityCandidate) city = cityCandidate;

      return { street, city, province, postalCode, country };
   };

   // Status strip: Interpret workflow steps
   const normalizeStageToken = (value) =>
      String(value ?? "")
         .trim()
         .replace(/[^A-Za-z_]/g, "")
         .toUpperCase();
   const pipelineStages =
      payloadInvSt?.pipeline?.stages ??
      payloadInvSt?.dpa_workflow_status?.pipeline?.stages ??
      setupData?.pipeline?.stages ??
      setupData?.stages ??
      [];
   const stageStatuses = pipelineStages.reduce((acc, stage) => {
      const stageName = normalizeStageToken(stage?.name);
      const stageStatus = normalizeStageToken(stage?.status);
      if (stageName && stageStatus) {
         acc[stageName] = stageStatus;
      }
      return acc;
   }, {});

   // Translate workflow status to display status
   const translateStatus = (status) => {
      if (!status) return "PENDING";
      if (status === "SUCCESS" || status === "COMPLETE") return "COMPLETE";
      if (status === "FAILED") return "FAILED";
      return status;
   };

   const supplierStatus = (panelConfig?.supplier_status || translateStatus(stageStatuses.SUPPLIER)).toUpperCase();
   const layoutStatus   = translateStatus(stageStatuses.LAYOUT);
   const panelData = setupData?.panel_data || setupData?.panelData || payloadInvSt?.panel_data || payloadInvSt || {};
   const isReadyToProcess =
      String(payloadInvSt?.status_title || "").trim().toLowerCase() === "ready to process" ||
      String(payloadInvSt?.status_message || "").trim().toLowerCase() === "onboarding is complete and the document is ready to be processed.";
   const setupButtonText = isReadyToProcess
      ? (isProcessing ? "Processing…" : "Process Document")
      : (toBool(panelData?.has_any_saved_activity) ? "Complete Setup" : "Start Setup");

   const supplierResolutionDraft = useMemo(() => {
      // The summary endpoint emits the literal "Not detected" for missing fields; it must
      // never enter the draft as a real name (the payee gate would scan it, and
      // include_on_save could publish it as an alias).
      const real = (v) => {
         const s = String(v || "").trim();
         return s && s !== "Not detected" ? s : "";
      };
      const parentName = real(panelConfig?.extracted?.parentName);
      const tradingName = real(panelConfig?.extracted?.tradingName);
      const parsedAddress = parseAddressParts(null, real(panelConfig?.extracted?.address));

      const observed = [];
      if (parentName) {
         observed.push({
            source_field: "parent_supplier_name",
            value: parentName,
            normalized_value: normalizeSupplierName(parentName),
            alias_type: "PARENT",
            exists: false,
            include_on_save: true,
         });
      }
      if (tradingName) {
         observed.push({
            source_field: "trading_supplier_name",
            value: tradingName,
            normalized_value: normalizeSupplierName(tradingName),
            alias_type: "TRADING",
            exists: false,
            include_on_save: true,
         });
      }

      return {
         canonical_supplier: {
            mode: null,
            supplier_id: null,
            draft_supplier: null,
         },
         address: {
            street: parsedAddress.street || "",
            city: parsedAddress.city || "",
            province: parsedAddress.province || "",
            postal_code: parsedAddress.postalCode || "",
            country: normalizeCountryCode(parsedAddress.country || "CA"),
            address_source: "alias",
            raw: panelConfig?.extracted?.address || "",
         },
         observed_names: observed,
      };
   }, [panelConfig]);

   useEffect(() => {
      const getInvoiceStatement = async () => {
         if (!dpaId) {
            setIsLoadingInvSt(false);
            setPayloadInvSt(null);
            return;
         }
         setIsLoadingInvSt(true);
         const url = "/getonboard_summary.sz";
         const method = "POST";
         const formData = new FormData();
         formData.append("_dpa_id", dpaId);

         try {
            const { responseData } = await fetchData(url, method, formData, "form-data");
            console.log("RESPONSEDATA:", responseData);
            setIsLoadingInvSt(false);
            if (responseData.st === 1) {
               if (responseData?.payload) {
                  const setupPayload = responseData?.payload || null;
                  const statementData = setupPayload?.statement || setupPayload;
                  setSetupData(setupPayload);
                  setPayloadInvSt(statementData);
               } else {
                  setSetupData(null);
                  setPayloadInvSt(null);
               }
            } else {
               triggerToast("warning", responseData.m_en || "Issue retrieving data", false, "", false);
            }
         } catch (error) {
            setIsLoadingInvSt(false);
            triggerToast("error", "We seem to have an issue accessing your data, please try again later.", true, "Network Issue", false, 0);
         }
      };
      getInvoiceStatement();
   }, [dpaId]);

   // Reset all state when a different document is selected
   useEffect(() => {
      setShowStatementPdfModal(false);
      setStatementImageData(null);
   }, [dpaId, payloadInvSt?.doc_id]);

   // The modal stays mounted across documents; clear the deleting flag when a
   // different document part is selected so it doesn't leak into the next one.
   useEffect(() => {
      setIsDeleting(false);
   }, [dpaId]);
   

   const convertBlobToBase64 = (blob) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.readAsDataURL(blob);
         reader.onloadend = () => resolve(reader.result.split(",")[1]);
         reader.onerror = reject;
      });
   };

   const fetchDocumentPartPdf = async () => {
      if (!dpaId) return null;

      const url = "/getfile.sz";
      const method = "POST";
      const formData = new FormData();
      formData.append("_dpa_id", dpaId);
      formData.append("_step", "preprocess");

      const { responseData, fetchError } = await fetchData(url, method, formData, "form-data", "blob");

      if (fetchError || !responseData) {
         throw new Error("Failed to retrieve statement PDF.");
      }

      return convertBlobToBase64(responseData);
   };

   const handleStatementView = async () => {
      if (!payloadInvSt?.doc_id && !dpaId) return;

      try {
         const base64Data = await fetchDocumentPartPdf();
         if (!base64Data) return;
         setStatementImageData(base64Data);
         setShowStatementPdfModal(true);
      } catch (error) {
         triggerToast("error", "Unable to open the statement right now. Please try again.", true, "Issue", false, 0);
      }
   };

   const handleInvoiceStatementDelete = () => {
      const question = `Are you sure you want to delete statement ${payloadInvSt?.doc_orig_name}`;
      dispatchModal({ type: "OPEN_MODAL", payload: { question: question } });
   };

   // we listen for the modal dialogue response which is a callback from ModalDialogue
   const handleModalResponse = async (response) => {
      if (response !== "go") {
         dispatchModal({ type: "CLOSE_MODAL" });
         return;
      }

      dispatchModal({ type: "CLOSE_MODAL" });

      if (!dpaId) {
         triggerToast("error", "Cannot delete: missing document part id.", true, "Error", false, 0);
         return;
      }

      setIsDeleting(true);

      try {
         const formData = new FormData();
         formData.append("_dpa_id", dpaId);

         const { responseData } = await fetchData("/deleteDoc.sz", "POST", formData, "form-data");

         if (responseData?.st === 1) {
            triggerToast("success", "Statement deleted.", false, "", false);
            onDeleted?.(dpaId);
         } else {
            setIsDeleting(false);
            triggerToast("error", responseData?.m_en || "Failed to delete statement.", true, "Error", false, 0);
         }
      } catch {
         setIsDeleting(false);
         triggerToast("error", "Network issue while deleting. Please try again.", true, "Network Issue", false, 0);
      }
   };

   const formatStatementDateLabel = (rawDate) => {
      if (!rawDate) return "Not detected";
      try {
         return format(parseISO(String(rawDate)), "MM/dd/yyyy");
      } catch {
         return String(rawDate);
      }
   };

   const handleStartOnboarding = async () => {
      if (isReadyToProcess) {
         if (!dpaId || isProcessing) return;
         setIsProcessing(true);
         try {
            const formData = new FormData();
            formData.append("_dpa_id", dpaId);
            const { responseData } = await fetchData("/process.sz", "POST", formData, "form-data");
            if (responseData?.st === 1) {
               triggerToast("success", responseData?.msg || responseData?.m_en || "Document sent for processing.", false, "Processing started", false);
            } else {
               triggerToast("warning", responseData?.msg || responseData?.m_en || "Unable to start processing.", false, "Issue", false);
            }
         } catch {
            triggerToast("error", "Unable to start processing right now. Please try again.", true, "Issue", false, 0);
         } finally {
            setIsProcessing(false);
         }
         return;
      }

      setShowConfigureModal(true);
      try {
         const base64Data = await fetchDocumentPartPdf();
         if (base64Data) setStatementImageData(base64Data);
      } catch {
         // non-fatal — modal opens without PDF preview
      }
   };

   const renderReviewPanel = () => {
      const supplierReady = supplierStatus === "COMPLETE";
      const statementRequired = [
         panelConfig?.extracted?.parentName || panelConfig?.extracted?.tradingName,
         payloadInvSt?.statement_date || payloadInvSt?.detected_statement_date,
         panelConfig?.extracted?.address,
      ];
      const missingStatementFields = statementRequired.filter((x) => !String(x || "").trim()).length;

      const inferredMissingMappings = Number(payloadInvSt?.missing_required_invoice_mappings);
      const missingInvoiceMappings = Number.isFinite(inferredMissingMappings)
         ? Math.max(0, inferredMissingMappings)
         : layoutStatus === "COMPLETE"
            ? 0
            : 1;

      const invoiceDetectedRaw = String(payloadInvSt?.invoice_table_detected ?? payloadInvSt?.has_invoice_table ?? "").toLowerCase();

      const quickPreviewRows = [
         { label: "Supplier Parent Name", value: panelConfig?.extracted?.parentName || "Not detected" },
         { label: "Supplier Trading Name", value: panelConfig?.extracted?.tradingName || "Not detected" },
         {
            label: "Statement Date",
            value: formatStatementDateLabel(payloadInvSt?.statement_date || payloadInvSt?.detected_statement_date),
         },
         { label: "Supplier Trading Address", value: panelConfig?.extracted?.address || "Not detected" },
         {
            label: "Invoice table detected",
            value:
               invoiceDetectedRaw === "yes" ||
               invoiceDetectedRaw === "true" ||
               invoiceDetectedRaw === "1"
                  ? "Yes"
                  : "No",
         },
      ];

      const statusSummaryLabel = payloadInvSt?.status_title || (supplierReady ? "Needs onboarding" : "Needs review before onboarding");

      return (
         <div style={{ paddingTop: "10px" }}>
            <div className="card-bc border-0 mb-3">
               <div className="card-body border-top border-1" style={{ "--card-body-content": "'Status'" }}>
                  <Row className="d-flex mt-3 align-items-start g-3">
                     <Col xs="auto" className="pt-1">
                        <FaExclamationCircle size={20} style={{ color: supplierReady ? "#0d6efd" : "#ffc107" }} />
                     </Col>
                     <Col>
                        <div className="fw-semibold">{statusSummaryLabel}</div>
                        <div className="small text-muted mt-1">
                           {payloadInvSt?.status_message ||
                              (supplierReady
                                 ? "Supplier is matched. Continue to onboarding to complete statement and invoice setup."
                                 : "Supplier fields need confirmation before onboarding can be completed.")}
                        </div>
                     </Col>
                  </Row>
               </div>
            </div>

            <div className="card-bc border-0 mb-3">
               <div className="card-body border-top border-1" style={{ "--card-body-content": "'Quick extracted preview'" }}>
                  <div className="mt-3">
                     {quickPreviewRows.map((field) => (
                        <div key={field.label} className="mb-3">
                           <div className="text-uppercase small text-muted mb-1">{field.label}</div>
                           <div className="fw-semibold text-wrap">{field.value}</div>
                        </div>
                     ))}
                  </div>
               </div>
            </div>

            <div className="card-bc border-0 mb-3">
               <div className="card-body border-top border-1" style={{ "--card-body-content": "'Readiness checklist'" }}>
                  <ul className="list-unstyled mt-3 mb-0 small">
                     <li className="mb-2">
                        <span className="fw-semibold">Supplier:</span>{" "}
                        {supplierReady ? "Ready" : "Needs review"}
                     </li>
                     <li className="mb-2">
                        <span className="fw-semibold">Statement:</span>{" "}
                        {missingStatementFields > 0 ? `Missing ${missingStatementFields} required field${missingStatementFields > 1 ? "s" : ""}` : "Ready"}
                     </li>
                     <li>
                        <span className="fw-semibold">Invoices:</span>{" "}
                        {missingInvoiceMappings > 0 ? `Missing ${missingInvoiceMappings} required mapping${missingInvoiceMappings > 1 ? "s" : ""}` : "Ready"}
                     </li>
                  </ul>
               </div>
            </div>

         </div>
      );
   };

   const renderComingSoonSection = (sectionName) => (
      <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: "300px" }}>
         <FaLock size={40} className="mb-3 opacity-50" />
         <h5 className="fw-semibold mb-1">Feature coming soon</h5>
         <p className="small">{sectionName} setup is not yet available.</p>
      </div>
   );

   const renderLayoutSection = () => (
      <div className="d-flex flex-column" style={{ minHeight: "300px" }}>
         <div id="summary-section" style={{ paddingTop: "10px" }}>
            <div className="card-bc border-0 mb-3">
               <div className="card-body border-top border-1" style={{ "--card-body-content": "'Layout'" }}>
                  <Row className="d-flex mt-3 align-items-center">
                     <Col>
                        <p className="mb-0 text-muted">
                           Let's walk through this statement so we can better understand its layout.
                        </p>
                     </Col>
                  </Row>
               </div>
            </div>
         </div>
         <div className="mt-auto d-flex justify-content-end">
            <Button variant="primary" onClick={() => {}}>
               Help Me Review Layout
            </Button>
         </div>
      </div>
   );

   const renderActiveSection = () => {
      return renderReviewPanel();
   };

   return (
      <>
      <Offcanvas style={{ top: "71px", zIndex: "799" }} show={show} scroll={true} backdrop={false} placement="end" className="offcanvas-width-invoice-statements">
         <Offcanvas.Header
               className="d-flex flex-column align-items-start w-100"
               style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1055,
               }}>
               <Row className="d-flex align-items-start flex-nowrap w-100">
                  <Col xs="auto" className="text-start">
                     {isLoadingInvSt ? (
                        <div className="placeholder-glow">
                           <span className="placeholder" style={{ width: "77px", height: "100px" }}></span>
                        </div>
                     ) : payloadInvSt === null ? (
                        <span></span>
                     ) : (
                        <div className="position-relative d-inline-block">
                           <button className="offcanvas-statement-preview offcanvas-statement-preview-selected border border-primary shadow-sm" onClick={handleStatementView} type="button" aria-label="View statement">
                              <img src={`https://storage.googleapis.com/0b4be1ac-452a-4db9-a8cc-a38082af5a60/${payloadInvSt?.doc_id || docId}.png`} className="img-fluid offcanvas-statement-preview-image" loading="lazy" alt="Document" />
                              <span className="offcanvas-statement-preview-view text-primary position-absolute top-50 start-50 translate-middle">
                                 View
                              </span>
                           </button>
                        </div>
                     )}
                  </Col>
                  <Col style={{ minWidth: 0 }}>
                     {isLoadingInvSt ? (
                        <div className="fs-5 mb-3 ms-2">
                           <div className="placeholder-glow">
                              <span className="placeholder" style={{ width: "100%", height: "25px" }}></span>
                           </div>
                        </div>
                     ) : (
                        <div
                           className="fs-5 mb-3 ms-2 position-relative"
                           style={{
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                           }}>
                           {(() => {
                              const real = (v) => (v && v !== "Not detected" ? v : null);
                              return payloadInvSt?.doc_orig_name
                                 || (real(payloadInvSt?.statement_date) ? `Statement — ${payloadInvSt.statement_date}` : null)
                                 || real(panelConfig?.extracted?.tradingName)
                                 || real(panelConfig?.extracted?.parentName)
                                 || "Invoice Statement";
                           })()}
                        </div>
                     )}
                     <div className="d-grid gap-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
                        {isLoadingInvSt ? (
                           <span style={{ width: "100%", height: "25px" }}> </span>
                        ) : (
                           <>
                              <button
                                 className="btn btn-link d-inline-flex align-items-center justify-content-center pt-1 pb-1 text-primary rounded-pill btn-link-offcanvas-border"
                                 title="Download"
                              >
                                 <FaCloudDownloadAlt size={16} className="me-2" />
                                 Download
                              </button>
                              <button
                                 onClick={handleInvoiceStatementDelete}
                                 disabled={isDeleting}
                                 aria-disabled={isDeleting}
                                 className="btn btn-link d-inline-flex align-items-center justify-content-center pt-1 pb-1 text-primary rounded-pill btn-link-offcanvas-border"
                                 title={isDeleting ? "Deleting..." : "Delete"}
                              >
                                 <FaTrash size={16} className="me-2" />
                                 {isDeleting ? "Deleting..." : "Delete"}
                              </button>
                           </>
                        )}
                     </div>
                  </Col>
               </Row>
            </Offcanvas.Header>

         <Offcanvas.Body>
            {renderActiveSection()}
         </Offcanvas.Body>

         <div className="border-top p-3 position-sticky bottom-0 w-100" style={{ zIndex: 1055 }}>
            <Row>
               <Col className="d-flex justify-content-end align-items-center">
                  <Button variant="primary" size="sm" onClick={handleStartOnboarding} disabled={isLoadingInvSt || !payloadInvSt || isProcessing}>
                     {setupButtonText}
                  </Button>
               </Col>
            </Row>
         </div>

         <InvoiceStatementConfigureModal
            show={showConfigureModal}
            onHide={() => setShowConfigureModal(false)}
            dpaId={dpaId}
            invStatId={payloadInvSt?.doc_id || dpaId}
            supplierResolutionDraft={supplierResolutionDraft}
            fileName={payloadInvSt?.doc_orig_name || "Document"}
            statementImageData={statementImageData}
            docObj={{
               name: payloadInvSt?.doc_orig_name,
               uploadOn: payloadInvSt?.doc_upload_on,
               uploadBy: payloadInvSt?.doc_upload_by,
            }}
         />

      </Offcanvas>
      {showStatementPdfModal && (
               <PdfModal
                  imageData={statementImageData}
                  propScale={1.0}
                  docObj={{
                     name: payloadInvSt?.doc_orig_name,
                     uploadOn: payloadInvSt?.doc_upload_on,
                     uploadBy: payloadInvSt?.doc_upload_by,
                  }}
                  onCloseModal={() => setShowStatementPdfModal(false)}
                  rightOffset="500px"
               />
            )}
      <ModalDialogue isModalDialogueOpen={isModalDialogueOpen} question={question} onModalDialogueResponse={handleModalResponse} />
      </>
   );
};

export default InvoiceStatementSetupModal;
